import { DateTime } from 'luxon'
import { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { PrivateCompanyIndicator } from '@components/PrivateCompanyIndicator'
import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import { ProfileAlert } from '@modules/alerts/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import useContactable from '@modules/profile/hooks/useContactable'
import {
  DelegateProfileInfo,
  ProfileName,
  ProfileSubTitle,
  ProfileTitle,
  ProfileTitleButtons,
  ProfileTitleInner,
  ProfileTitleWrapper,
  StyledHeaderProfileLogo,
} from '@modules/profile/index.styles'
import { AnyResultProfile, HistoryProfileEvent, ProfileType } from '@modules/search/types'
import ShortlistProfileButton from '@modules/shortlist/components/ShortlistProfileButton'
import { Box, Button } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQuery } from '@tanstack/react-query'

import searchApi from '../search/api'
import { EventInfo } from './EventInfo'
import useAttendees from './hooks/useAttendees'

export type ProfileHeaderProps = {
  profile: AnyResultProfile
  profileName: {
    title?: string
    firstName?: string
    lastName?: string
  }
  updatedAt?: string
  profileJobTitle?: string
  profileDivision?: string
  profileType: ProfileType
  profileAlert?: ProfileAlert
  isPrivate?: boolean
  companyId?: number
  isAllDelegatesPageEnabled?: boolean
  events?: HistoryProfileEvent[]
}

const ProfileHeader = (props: ProfileHeaderProps) => {
  const {
    profile,
    profileName: { title, firstName, lastName } = {},
    updatedAt = '',
    profileJobTitle,
    profileDivision,
    profileType,
    profileAlert,
    isPrivate,
    companyId,
    events,
    isAllDelegatesPageEnabled = true,
  } = props
  const { t } = useTranslation()

  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()

  const { openChatModal } = useChatContext()
  const { hasContactedWithCompany, hasContactedDelegate } = useAttendees({
    companyId: companyId!,
    delegateId: profile.id,
    eventId: profile.latest.eventData?.id,
  })

  const isDelegateProfile = useMemo(() => profileType === ProfileType.delegate, [profileType])

  const { data: attendeesCounts } = useQuery({
    queryKey: [searchApi.getAttendeesCount.key, companyId],
    queryFn: () => searchApi.getAttendeesCount.fn({ companyId }),
    enabled: !isDelegateProfile && isAllDelegatesPageEnabled,
  })

  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor?: Nullable<HTMLElement>
      profile?: AnyResultProfile
    }>
  >(null)
  const contactableMap = useContactable(profile ? [Number(companyId)] : [])

  const withProfileLogo = [ProfileType.company, ProfileType.delegate].includes(profileType)

  const isContactButtonEnabled = useMemo(() => {
    if (!contactableMap || !profile) return false
    return contactableMap[Number(companyId)]
  }, [contactableMap, profile, companyId])

  const shouldShowContactedBubble = useMemo(
    () => (isDelegateProfile ? hasContactedDelegate : hasContactedWithCompany),
    [hasContactedWithCompany, hasContactedDelegate, isDelegateProfile],
  )

  const futureEventsCount = useMemo(() => {
    if (!isDelegateProfile) {
      return attendeesCounts?.events?.future
    }

    return events?.reduce((acc, event) => {
      if (event.companyId !== companyId) {
        return acc
      }

      if (DateTime.now().startOf('day') <= DateTime.fromISO(event.endDate)) {
        return acc + 1
      }
      return acc
    }, 0)
  }, [isDelegateProfile, events, attendeesCounts, companyId])

  const pastEventsCount = useMemo(() => {
    if (!isDelegateProfile) {
      return attendeesCounts?.events?.past
    }

    return events?.reduce((acc, event) => {
      if (event.companyId !== companyId) {
        return acc
      }

      if (DateTime.now().startOf('day') > DateTime.fromISO(event.endDate)) {
        return acc + 1
      }
      return acc
    }, 0)
  }, [isDelegateProfile, events, attendeesCounts, companyId])

  const onContactClick = (e: MouseEvent<HTMLElement>) => {
    if (profile['@type'] === ProfileType.delegate && profile.latest.id !== user?.id) {
      return openChatModal({ delegates: [profile.latest], companyId: profile.latest.companyId, chatType: ChatType.DIRECT })
    }

    if (e?.currentTarget) {
      setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile })
      return null
    }

    return setContactAttendeesPopoverData(null)
  }
  const shouldShowContactButton =
    profile.latest.id !== user?.id &&
    hasAccessToChat &&
    profile.hasPublicProfile &&
    !isEcommerce &&
    isContactButtonEnabled &&
    (!isDelegateProfile || profile.isContactable)

  const contactButtonId = `contact_button-${profileType === ProfileType.delegate ? profileType : ProfileType.company}`

  return (
    <ProfileTitleWrapper>
      <ProfileTitleInner fullWidth={isDelegateProfile}>
        <ProfileTitle>
          <>
            {withProfileLogo && (
              <StyledHeaderProfileLogo
                profileData={profile.latest}
                profileType={profileType}
                width={isDelegateProfile ? 90 : 65}
                defaultLogoSize={43}
                circleView
              />
            )}
            <div>
              <ProfileName variant="h2">
                {title} {firstName} {lastName}
                {isPrivate && (
                  <Box sx={{ marginLeft: '10px', display: 'inline' }}>
                    <PrivateCompanyIndicator />
                  </Box>
                )}
              </ProfileName>
              {profileJobTitle && <ProfileSubTitle variant="h2">{profileJobTitle}</ProfileSubTitle>}
              {profileDivision && <Typography variant="body1">{profileDivision}</Typography>}
              {!isDelegateProfile && (
                <EventInfo
                  isDelegate={isDelegateProfile}
                  updatedAt={updatedAt}
                  companyId={companyId}
                  delegatesCount={attendeesCounts?.count}
                  futureEventsCount={futureEventsCount}
                  pastEventsCount={pastEventsCount}
                />
              )}
            </div>
          </>
        </ProfileTitle>
        {isDelegateProfile && (
          <DelegateProfileInfo>
            <EventInfo
              isDelegate={isDelegateProfile}
              companyId={companyId}
              updatedAt={updatedAt}
              futureEventsCount={futureEventsCount}
              pastEventsCount={pastEventsCount}
            />
          </DelegateProfileInfo>
        )}
      </ProfileTitleInner>
      <ProfileTitleButtons>
        {hasAccessToAlerts && (
          <ProfileAlertButton
            profile={profile}
            withLastViewedUpdate
            savedProfileAlert={profileAlert}
            isPrivate={!profile.hasPublicProfile}
          />
        )}
        <ShortlistProfileButton profile={profile.latest} location="delegate_profile" />
        {shouldShowContactButton && (
          <ContactedBubble hasContacted={shouldShowContactedBubble}>
            <Button
              sx={{ marginLeft: '10px' }}
              variant="contained"
              data-testid="contact_btn"
              onClick={onContactClick}
              id={contactButtonId}
            >
              {t(translationKeys.CONTACT_BUTTON)}
            </Button>
          </ContactedBubble>
        )}
      </ProfileTitleButtons>

      {!!contactAttendeesPopoverData?.profile && (
        <ContactAttendeesPopover
          anchor={contactAttendeesPopoverData?.anchor}
          profile={contactAttendeesPopoverData?.profile}
          close={() => setContactAttendeesPopoverData(null)}
          openChatModal={openChatModal}
        />
      )}
    </ProfileTitleWrapper>
  )
}

export default ProfileHeader
